import {
  HiOutlineLocationMarker,
  HiOutlineClipboardList,
  HiOutlineCalendar,
  HiOutlineShoppingBag,
  HiOutlineCurrencyRupee,
} from "react-icons/hi";
import { FaTimes, FaShippingFast, FaChartLine, FaImage } from "react-icons/fa";
import { formatToINR, generateTitle, getFileUrl, getMainImageUrl } from "../../../utils/helper";
import placeHolderImage from "../../../assets/images/placeholder/product.jpg";
import { getStatusBadgeClass } from "./OrderList";

const ViewOrder = ({ order, closeModal }) => {
  if (!order) return null;
  const latestStatus = order?.reports?.[order.reports.length - 1]?.status;
  const statusOrder = ["Lead Requested", "Dispatched", "Completed"];
  const isCanceled = order.reports?.some(
    (report) => report.status === "Cancelled"
  );

  const dispatchedReport = order.reports?.find(
    (report) => report.status === "Cancelled"
  );
  const remark = dispatchedReport?.remark || "";
  const currentIndex = statusOrder.indexOf(latestStatus);
  const progressPercentage =
    currentIndex >= 0 ? (currentIndex / (statusOrder.length - 1)) * 100 : 0;

  const badge = (text) => (
    <span
      className={`inline-flex items-center px-3 py-1 rounded text-xs font-bold uppercase tracking-wider ${
        getStatusBadgeClass(text) || "bg-gray-100 text-gray-800"
      }`}
    >
      {text}
    </span>
  );

  return (
    <div className="relative w-full text-gray-700 border shadow-2xl bg-gradient-to-b from-gray-50 to-white rounded-xl border-emerald-100">
      {/* Compact Header */}
      <div className="sticky top-0 z-10 p-4 bg-gradient-to-r from-emerald-500 to-emerald-700 ">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-lg">
              <HiOutlineClipboardList className="text-lg text-white" />
            </div>
            <h2 className="text-lg font-bold text-white">
              Order #{order.orderId}
            </h2>
          </div>
          <button
            onClick={closeModal}
            className="p-1.5 text-white/80 hover:text-white rounded-full transition-colors duration-200"
            aria-label="Close"
          >
            <FaTimes size={18} />
          </button>
        </div>

        <div className="flex items-center justify-between mt-2 text-xs text-emerald-100">
          <div className="flex items-center gap-1.5">
            <HiOutlineCalendar className="text-xs" />
            <span>{new Date(order.createdAt).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <FaShippingFast className="text-xs" />
            <span>Express Delivery</span>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Status Card */}
        <div className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold flex items-center gap-1.5 text-gray-700">
              <FaShippingFast className="text-emerald-500" />
              Order Status
            </h3>
            <div>{badge(latestStatus)}</div>
          </div>

          {!isCanceled ? (
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Order Progress</span>
                <span className="text-xs font-medium text-emerald-600">
                  {Math.round(progressPercentage)}%
                </span>
              </div>

              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full transition-all duration-700 bg-gradient-to-r from-emerald-400 to-emerald-600"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>

              <div className="flex justify-between mt-3 overflow-x-auto text-xs text-center text-nowrap">
                {statusOrder.map((status, index) => (
                  <div
                    key={status}
                    className={`py-1.5 px-2 rounded ${
                      currentIndex >= index
                        ? "bg-emerald-50 text-emerald-700 font-medium"
                        : "text-gray-400"
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="py-2 pl-1 m-2 text-sm text-red-500 border border-red-400">
              {remark}
            </p>
          )}
        </div>

        {/* Products Section */}
        <div className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold flex items-center gap-1.5 text-gray-700">
              <HiOutlineShoppingBag className="text-emerald-500" />
              Products ({order.products.length})
            </h3>
          </div>

          <div className="divide-y divide-gray-100">
            {order.products.map((item) => {
              return (
                <div
                  key={item._id}
                  className="flex gap-3 p-3 transition-colors hover:bg-gray-50"
                >
                  <div className="flex-shrink-0">
                    <div className="relative w-16 h-16">
                      <img
                         src={getMainImageUrl(item?.product?.image)}
                        alt="product"
                        className="object-contain w-full h-full border-2 border-dashed rounded-xl"
                      />
                      <span className="absolute flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded-full -top-1 -right-1 bg-emerald-500">
                        {item.quantity}
                      </span>
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h4 className="font-medium text-gray-800">
                      {generateTitle(item?.product)}
                    </h4>
                    {item?.product?.productIdentificationNo && (
                      <p className="mt-1 text-xs text-gray-500">
                        Product ID: {item?.product?.productIdentificationNo || "N/A"}
                      </p>
                    )}
                    {item?.imei[0] && (
                      <p className="mt-1 text-xs text-gray-500">
                        IMEI 1/Serial No.: {item?.imei[0] || "N/A"}
                      </p>
                    )}
                    {item?.imei[1] && (
                      <p className="mt-1 text-xs text-gray-500">
                        IMEI 2/Serial No.: {item?.imei[1] || "N/A"}
                      </p>
                    )}
                    <div className="flex items-center mt-1 space-x-2 text-xs text-gray-500">
                      <span>IMEI/Serial No:</span>
                      {item?.isConfirmed ? (
                        <span className="ml-2 inline-block bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full font-medium">
                          Confirmed
                        </span>
                      ) : (
                        <span className="ml-2 inline-block bg-red-100 text-red-700 text-[10px] px-2 py-0.5 rounded-full font-medium">
                          Not Confirmed
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex-shrink-0 text-right">
                    <p className="font-semibold text-gray-800">
                      {formatToINR(item.priceAtPurchase)}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Total: {formatToINR(item.priceAtPurchase * item.quantity)}
                    </p>
                    {item.image && (
                      <a
                        target="_blank"
                        href={getFileUrl(item.image)}
                        className="inline-flex items-center gap-1 mt-2 text-xs font-medium transition text-emerald-600 hover:text-emerald-800"
                      >
                        <FaImage />
                        View IMEI/Serial No.
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 gap-5">
          {/* Shipping Address */}
          <div className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="p-3 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-semibold flex items-center gap-1.5 text-gray-700">
                <HiOutlineLocationMarker className="text-emerald-500" />
                Shipping Address
              </h3>
            </div>

            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="p-2 rounded-full bg-emerald-100">
                    <HiOutlineLocationMarker className="text-emerald-600" />
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-800">
                    {order.address.name}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1.5">
                    {order.address.addressLine1}
                    {order.address.addressLine2 && (
                      <>, {order.address.addressLine2}</>
                    )}
                    <br />
                    {order.address.city}, {order.address.state} -{" "}
                    {order.address.pincode}
                    <br />
                    {order.address.country}
                  </p>
                  <div className="mt-3 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Phone:</span>
                      <span>{order.address.phone}</span>
                    </div>
                    {order.address.landmark && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-medium">Landmark:</span>
                        <span>{order.address.landmark}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order Timeline */}
          <div className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="p-3 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-semibold flex items-center gap-1.5 text-gray-700">
                <FaChartLine className="text-emerald-500" />
                Order Timeline
              </h3>
            </div>

            <div className="p-4">
              {order.reports.length > 0 ? (
                <div className="relative">
                  <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-gray-200"></div>

                  <div className="space-y-3">
                    {order.reports.map((report, idx) => (
                      <div key={idx} className="relative flex items-start pl-6">
                        <div className="absolute left-4 mt-2 -ml-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white shadow-sm"></div>
                        <div className="flex-grow">
                          <div className="flex items-center justify-between">
                            <div className="font-medium text-gray-800">
                              {report.status.charAt(0).toUpperCase() +
                                report.status.slice(1)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(report.reportedAt).toLocaleTimeString(
                                [],
                                { hour: "2-digit", minute: "2-digit" }
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mt-0.5">
                            {report.remark || ""}
                          </p>
                          <div className="text-xs text-gray-400 mt-0.5">
                            {new Date(report.reportedAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="py-4 text-center">
                  <div className="inline-flex p-3 bg-gray-100 rounded-full">
                    <HiOutlineCalendar className="text-xl text-gray-400" />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    No timeline events available
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="overflow-hidden border rounded-lg shadow-sm bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-100">
          <div className="p-3 border-b bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 border-emerald-200">
            <h3 className="text-sm font-semibold flex items-center gap-1.5 text-emerald-800">
              <HiOutlineCurrencyRupee className="text-emerald-600" />
              Order Summary
            </h3>
          </div>

          <div className="p-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-white border border-gray-200 rounded">
                <div className="text-xs text-gray-500">Items</div>
                <div className="text-base font-bold text-gray-800">
                  {order.products.length}
                </div>
              </div>

              <div className="p-3 bg-white border border-gray-200 rounded">
                <div className="text-xs text-gray-500">Shipping</div>
                <div className="text-base font-bold text-gray-800">
                  {formatToINR(order.shippingCost || 0)}
                </div>
              </div>

              <div className="p-3 bg-white border border-gray-200 rounded">
                <div className="text-xs text-gray-500">Discount</div>
                <div className="text-base font-bold text-emerald-600">
                  -{formatToINR(order.discount || 0)}
                </div>
              </div>

              <div className="p-3 rounded shadow-sm bg-gradient-to-r from-emerald-500 to-emerald-600">
                <div className="text-xs text-emerald-100">Total Amount</div>
                <div className="text-lg font-bold text-white">
                  {formatToINR(order.totalAmount)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewOrder;
